import { ChangeEvent, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  Group,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { IconLock, IconSearch, IconWorld } from '@tabler/icons-react'
import { EntityParams } from 'app/models/entity-params'
import { FundGroup, FundGroupVisibility } from 'app/models/fund-group'
import { FUND_GROUP_COPY } from './fund-group-copy'
import {
  FundGroupFormErrors,
  FundGroupFormValues,
  orderEntitiesPinnedFirst,
  validateGroupForm,
} from './fund-group-utils'

/** What the form hands back on submit; the caller decides create vs update. */
export type FundGroupSubmitResult =
  { ok: true } | { ok: false; field: 'name' | 'entityIds'; message: string }

type Props = {
  initial?: FundGroup
  entities: EntityParams[]
  existingGroups: Pick<FundGroup, 'id' | 'name'>[]
  onSubmit: (values: FundGroupFormValues) => Promise<FundGroupSubmitResult>
  onCancel: () => void
}

const VISIBILITY_OPTIONS = [
  {
    value: 'Private',
    label: (
      <span className="fund-group-visibility-option">
        <IconLock size={18} /> {FUND_GROUP_COPY.privateLabel}
      </span>
    ),
  },
  {
    value: 'Public',
    label: (
      <span className="fund-group-visibility-option">
        <IconWorld size={18} /> {FUND_GROUP_COPY.publicLabel}
      </span>
    ),
  },
]

/**
 * Create / edit form for an allocation group. The fund manager is NOT a field: inside a trade's
 * allocation editor it is the trade's own counterparty, and `entities` are already that
 * manager's funds. Validation is inline; server-side rejections (duplicate name → 409, a member
 * the ledger refuses → 400) come back through `onSubmit` and land on the matching field.
 */
export function FundGroupForm({
  initial,
  entities,
  existingGroups,
  onSubmit,
  onCancel,
}: Props) {
  const [values, setValues] = useState<FundGroupFormValues>({
    name: initial?.name ?? '',
    visibility: initial?.visibility ?? 'Private',
    entityIds: initial?.entityIds ?? [],
  })
  const [errors, setErrors] = useState<FundGroupFormErrors>({})
  const [query, setQuery] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // On edit, the group's members as opened sit at the top of the list.
  const orderedEntities = useMemo(
    () => orderEntitiesPinnedFirst(entities, initial?.entityIds ?? []),
    [entities, initial?.entityIds],
  )

  const visibleEntities = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return orderedEntities
    return orderedEntities.filter(
      e =>
        e.entityName.toLowerCase().includes(q) ||
        (e.mei ?? '').toLowerCase().includes(q),
    )
  }, [orderedEntities, query])

  const selected = useMemo(() => new Set(values.entityIds), [values.entityIds])

  const toggle = (id: string, checked: boolean) => {
    setValues(v => ({
      ...v,
      entityIds: checked
        ? [...v.entityIds.filter(x => x !== id), id]
        : v.entityIds.filter(x => x !== id),
    }))
    if (errors.entityIds) setErrors(e => ({ ...e, entityIds: undefined }))
  }

  const submit = async () => {
    const next = validateGroupForm(values, existingGroups, initial?.id)
    setErrors(next)
    if (next.name || next.entityIds) return
    setSubmitting(true)
    const result = await onSubmit({ ...values, name: values.name.trim() })
    setSubmitting(false)
    if (!result.ok) setErrors({ [result.field]: result.message })
  }

  return (
    <Stack spacing={22} className="fund-group-form">
      <TextInput
        label={FUND_GROUP_COPY.nameLabel}
        withAsterisk
        size="lg"
        classNames={{ label: 'fund-group-form-label' }}
        value={values.name}
        error={errors.name}
        onChange={e => {
          const name = e.currentTarget.value
          setValues(v => ({ ...v, name }))
          if (errors.name) setErrors(er => ({ ...er, name: undefined }))
        }}
        data-testid="fund-group-name"
      />

      <div>
        <Text className="fund-group-form-label">
          {FUND_GROUP_COPY.visibilityLabel}
        </Text>
        <SegmentedControl
          className="fund-group-visibility"
          data={VISIBILITY_OPTIONS}
          value={values.visibility}
          onChange={value =>
            setValues(v => ({ ...v, visibility: value as FundGroupVisibility }))
          }
        />
        <Text className="fund-group-form-help">
          {values.visibility === 'Private'
            ? FUND_GROUP_COPY.privateHelp
            : FUND_GROUP_COPY.publicHelp}
        </Text>
      </div>

      <div>
        <Text className="fund-group-form-label">
          {FUND_GROUP_COPY.entitiesLabel}{' '}
          <span className="fund-group-required">*</span>
        </Text>
        <TextInput
          size="md"
          icon={<IconSearch size={16} />}
          placeholder={FUND_GROUP_COPY.searchEntities}
          value={query}
          onChange={e => setQuery(e.currentTarget.value)}
          mb={10}
        />
        <ScrollArea.Autosize
          mah={340}
          className={`fund-group-entity-list${errors.entityIds ? ' has-error' : ''}`}
        >
          {entities.length === 0 && (
            <Text className="fund-group-form-help" p="md">
              {FUND_GROUP_COPY.noEntitiesForManager}
            </Text>
          )}
          {visibleEntities.map(entity => {
            const id = entity.id as string
            const checked = selected.has(id)
            return (
              <label
                key={id}
                className={`fund-group-entity-row${checked ? ' is-selected' : ''}`}
              >
                <Checkbox
                  checked={checked}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    toggle(id, e.currentTarget.checked)
                  }
                  size="md"
                  aria-label={entity.entityName}
                />
                <div>
                  <Text className="fund-group-entity-name">
                    {entity.entityName}
                  </Text>
                  {entity.mei && (
                    <Text className="fund-group-entity-mei">
                      MEI: {entity.mei}
                    </Text>
                  )}
                </div>
              </label>
            )
          })}
        </ScrollArea.Autosize>
        {errors.entityIds && (
          <Text className="fund-group-form-error">{errors.entityIds}</Text>
        )}
        <Text className="fund-group-form-selected">
          {FUND_GROUP_COPY.selected(values.entityIds.length)}
        </Text>
      </div>

      <Group position="right" spacing={16} className="fund-group-form-footer">
        <Button
          className="fund-group-footer-button fund-group-cancel-button"
          variant="default"
          size="lg"
          onClick={onCancel}
          disabled={submitting}
        >
          {FUND_GROUP_COPY.cancel}
        </Button>
        <Button
          className="fund-group-footer-button fund-group-primary-button"
          size="lg"
          loading={submitting}
          onClick={submit}
        >
          {FUND_GROUP_COPY.save}
        </Button>
      </Group>
    </Stack>
  )
}
